import {
	AfterViewInit,
	Component,
	Input,
	Output,
	EventEmitter,
	OnDestroy,
	ViewChild,
	OnInit,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSortable, MatSort, MatSortHeader } from '@angular/material/sort';
import {
	MatTableDataSource,
	MatTable,
	MatNoDataRow,
	MatColumnDef,
	MatHeaderCellDef,
	MatHeaderCell,
	MatCellDef,
	MatCell,
	MatHeaderRowDef,
	MatHeaderRow,
	MatRowDef,
	MatRow,
} from '@angular/material/table';
import { Subscription } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';
import { DataSetComponent } from 'src/app/components/data-set/data-set.component';
import { Device } from 'src/app/models/data/device';
import { AccessCodeListResponse } from 'src/app/models/data/accessCode';
import { FeatureFlagName } from 'src/app/models/data/featureFlag';
import { SystemCapabilityEnum } from 'src/app/models/data/systemCapability';
import { UserProfile } from 'src/app/models/model';
import {
	ApiLoadingKeys,
	ApiService,
	QueryParam,
} from 'src/app/services/api.services';
import { LoadingIndicatorService } from 'src/app/services/loading-indicator.service';
import { SystemCapabilityService } from 'src/app/services/system-capabilities/system-capability.service';
import { DeviceGenerateCodeDialogComponent } from 'src/app/components/device-generate-code-dialog/device-generate-code-dialog.component';
import { TimeZoneService } from 'src/app/services/timezone.service';
import { LocaleService } from 'src/app/services/locale.service';
import { DeviceUpdatedEvent } from '../device-details.component';
import { CodeFormatService } from 'src/app/services/code-format.service';
import { NotAuthorizedNoticeComponent } from '../../../../components/not-authorized-notice/not-authorized-notice.component';
import {
	DataSetComponent as DataSetComponent_1,
	DataSetToolbarComponent,
	DataSetSearchActionGroupDirective,
	DataSetPrimaryActionGroupDirective,
	DataSetActionButtonComponent,
	DataSetContentComponent,
} from '../../../../components/data-set/data-set.component';
import { HasSystemCapabilityDirective } from '../../../../directives/system-capabilities/has-system-capability.directive';
import { DataSetSearchComponent } from '../../../../components/data-set/data-set-search.component';
import {
	MatFormField,
	MatLabel,
	MatSuffix,
} from '@angular/material/form-field';
import { MatSelect, MatOption } from '@angular/material/select';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { MatInput } from '@angular/material/input';
import {
	MatDatepickerInput,
	MatDatepickerToggle,
	MatDatepicker,
} from '@angular/material/datepicker';
import { FormsModule } from '@angular/forms';
import { DataSetTableComponent } from '../../../../components/data-set/data-set-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { DatePipe } from '@angular/common';
import {
	AutoFormatPhoneNumberPipe,
	AccessDatePipe,
} from '../../../../components/pipes';
import { DataSetLoadingRowComponent } from '../../../../components/data-set/data-set-loading-row.component';
@Component({
	selector: 'app-device-generate-code',
	templateUrl: './device-generate-code.component.html',
	styleUrls: [
		'./device-generate-code.component.scss',
		'../../../common/main.scss',
	],
	imports: [
		NotAuthorizedNoticeComponent,
		DataSetComponent_1,
		HasSystemCapabilityDirective,
		DataSetToolbarComponent,
		DataSetSearchActionGroupDirective,
		DataSetSearchComponent,
		MatFormField,
		MatLabel,
		MatSelect,
		MatOption,
		MatIcon,
		MatTooltip,
		MatInput,
		MatDatepickerInput,
		FormsModule,
		MatDatepickerToggle,
		MatSuffix,
		MatDatepicker,
		DataSetPrimaryActionGroupDirective,
		DataSetActionButtonComponent,
		DataSetContentComponent,
		DataSetTableComponent,
		MatTable,
		MatSort,
		MatNoDataRow,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatSortHeader,
		MatCellDef,
		MatCell,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		DatePipe,
		AutoFormatPhoneNumberPipe,
		AccessDatePipe,
		DataSetLoadingRowComponent,
	],
})
export class DeviceGenerateCodeComponent
	implements AfterViewInit, OnDestroy, OnInit
{
	@ViewChild(DataSetComponent) dataSet!: DataSetComponent;
	@Input() deviceId!: string;
	@Input() userProfile!: UserProfile;
	@Input() device!: Device;
	@Input() allowCodeGeneration: boolean = false;
	@Output() deviceUpdatedEvents = new EventEmitter<DeviceUpdatedEvent>();

	displayedColumns: string[] = [
		'dateGenerated',
		'generatedBy',
		'assignedTo',
		'dateValid',
		'dateEnd',
		'reason',
		'rowMenu',
		'accessCode',
	];
	dataSource = new MatTableDataSource<AccessCodeListResponse>();

	ACCESS_CODE_TYPE = '1';
	HASP_CODE_TYPE = '2';

	canIssueFlexCodes: boolean = false;
	isHaspCodeGenFeatureEnabled: boolean = false;
	currentFilters: QueryParam[] | null = null;

	filterSubscription?: Subscription;

	accessCodeTooltip = $localize`:@@deviceGenerateCode-accessCodeTooltip:Access Code`;
	haspCodeTooltip = $localize`:@@deviceGenerateCode-haspCodeTooltip:Hasp Code`;

	codeTypeIds = [
		{ label: 'Access', value: '1' },
		{ label: 'Hasp', value: '2' },
	];

	filterLabels = {
		defaultPlaceholder: $localize`:@@deviceGenerateCode-defaultPlaceholder:Search assigned to name...`,
		codeType: $localize`:@@deviceGenerateCode-filterLabelCodeType:Type`,
		dateGenerated: $localize`:@@deviceGenerateCode-filterLabelDateGenerated:Date Generated`,
		generatedBy: $localize`:@@deviceGenerateCode-filterLabelGeneratedBy:Generated By`,
		assignedTo: $localize`:@@deviceGenerateCode-filterLabelAssignedTo:Assigned To`,
		dateValid: $localize`:@@deviceGenerateCode-filterLabelDateValid:Date Valid`,
		reason: $localize`:@@deviceGenerateCode-filterLabelReason:Reason`,
	};

	getAccessCodeLoadingKey = ApiLoadingKeys.getAccessCode;
	finishedLoadingInitData: boolean = false;

	constructor(
		private readonly apiService: ApiService,
		private readonly dialog: MatDialog,
		private readonly scService: SystemCapabilityService,
		public readonly loadingService: LoadingIndicatorService,
		readonly timeZoneService: TimeZoneService,
		readonly localeService: LocaleService,
		readonly codeFormatService: CodeFormatService
	) {}

	async ngOnInit(): Promise<void> {
		this.canIssueFlexCodes = this.scService.has(
			SystemCapabilityEnum.issueFlexCodes
		);
		await this.featureFlagCheck();
	}

	ngAfterViewInit(): void {
		this.dataSet.applyFilters();
		this.filterSubscription = this.dataSet.queryParams
			.pipe(
				map((filters) =>
					filters.map((it) => {
						const { param, value }: QueryParam = it;
						if ((value as any) instanceof Date) {
							return {
								param,
								value: (value as unknown as Date).toISOString(),
							};
						}
						return it;
					})
				),
				mergeMap(async (filter) => {
					this.currentFilters = filter;
					const result = await this.apiService.getAccessCode(
						this.deviceId,
						filter
					);
					this.finishedLoadingInitData = true;
					return result;
				})
			)
			.subscribe((data) => {
				this.dataSource.data = data;
			});
		Promise.resolve().then(() =>
			this.dataSource.sort?.sort({
				id: 'dateGenerated',
				start: 'desc',
			} as MatSortable)
		);
	}

	ngOnDestroy(): void {
		this.filterSubscription?.unsubscribe();
		this.dialog.closeAll();
	}

	async featureFlagCheck(): Promise<void> {
		this.isHaspCodeGenFeatureEnabled = (
			await this.apiService.getFeatureFlagByName(FeatureFlagName.webHaspCodes)
		)?.value;
		if (this.isHaspCodeGenFeatureEnabled) {
			this.displayedColumns = [...['codeTypeId'], ...this.displayedColumns];
		}
	}

	getShortenedText(text: string, length: number): string {
		if (text.length > length) {
			return text.substring(0, 50) + '...';
		}
		return text;
	}

	formatCodeIfNeeded(
		code: string | undefined,
		codeType: string
	): string | undefined {
		let formattedCode = code;
		// for a Gen3 hasp code we will format it, for all others we will leave it as is
		if (
			code &&
			codeType === this.HASP_CODE_TYPE &&
			this.device?.deviceGeneration?.toLowerCase()?.includes('gen3')
		) {
			formattedCode = this.codeFormatService.formatGen3HaspCode(code);
		}

		return formattedCode;
	}

	onGenerateCodeClick(): void {
		this.dialog
			.open(DeviceGenerateCodeDialogComponent, {
				panelClass: 'drawer-dialog',
				data: {
					deviceId: this.deviceId,
					userProfile: this.userProfile,
					device: this.device,
					fromDeviceScreen: true,
				},
			})
			.afterClosed()
			.subscribe((dialogResult: any) => {
				if (dialogResult?.deviceInfoChanged) {
					// some of the device information changed so we need to notify the parent component so the device
					// can be pulled from the server again
					this.deviceUpdatedEvents.emit({
						deviceId: this.device.id,
						updated: true,
					});
				}
				if (dialogResult?.codeGenerated) {
					this.dataSet?.applyFilters();
				}
			});
	}

	getApiService() {
		return this.apiService;
	}

	async onDownloadClicked(): Promise<void> {
		if (this.currentFilters) {
			const fileName = 'accessCodeReport';
			await this.apiService.generateSingleDeviceCodeGenReportCsv(
				fileName,
				this.deviceId,
				this.currentFilters
			);
		}
	}

	rowId(row: any): string {
		return row?.id?.toString() || 'unknown-id';
	}
}

@Component({
	selector: 'app-device-generate-code-box',
	template: `
		<!-- US12345: Make the entire background $color-background-secondary. No white infield -->
		<!-- <mat-form-field appearance="outline" class="custom-one-day-code-textbox">
		<input matInput class="custom-one-day-code-text" placeholder="82301 2938 1038" [(ngModel)]="oneDayCode" disabled="true">
		</mat-form-field> -->
		<div class="row custom-one-day-code-textbox">
			@if (showIcon && !isPDQDevice) {
				<mat-icon
					class="hasp-icon"
					svgIcon="sl-hasp-code-gen-filled"
				></mat-icon>
			}
			@if (!showIcon && !isPDQDevice) {
				<mat-icon
					class="code-icon"
					svgIcon="sl-access-code-gen-filled"
				></mat-icon>
			}
			@if (isPDQDevice) {
				<mat-icon
					class="code-icon senturian-icon"
					svgIcon="sl-senturian-lock"
				></mat-icon>
			}
			<p class="custom-one-day-code-text">
				{{ !!oneDayCode ? oneDayCode : '82301 2938 1038' }}
			</p>
		</div>
	`,
	styleUrls: [
		'./device-generate-code.component.scss',
		'../../../common/main.scss',
	],
	imports: [MatIcon],
})
export class DeviceGenerateCodeBoxComponent {
	@Input() oneDayCode: string | undefined;
	@Input() showIcon: boolean = false;
	@Input() isPDQDevice: boolean = false;

	constructor() {
		this.oneDayCode = this.oneDayCode?.trim();
	}
}
